import { useCallback, useEffect, useRef, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import {
    MoreHorizontal,
    Bookmark,
    Share2,
    MessageSquareText,
} from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import {
    Drawer,
    DrawerContent,
    DrawerTitle,
    DrawerDescription,
} from '@rocco/components/drawer';
import { uiContextAtomDisplayMode } from '@rocco/states/uiContextAtomDisplayMode';
import { uiStateAtomSocialSharingModalIsOpened } from '@rocco/ui/social-sharing/components/SocialSharingModal';
import { useFollowSelectorProxy } from '~/modules/moodboards/hooks/use-follow-selector-proxy';
import {
    isNativeShareAvailable,
    shareNative,
    type ShareOptions,
} from '~/modules/social-sharing/utils/sharing';
import { shareTargetAtom } from '~/modules/social-sharing/state/share-target';
import type { PinItemType } from '~/modules/moodboards/type';
import {
    useTileEnquiry,
    type TileEnquiryConfig,
} from '~/modules/enquiry/hooks/use-tile-enquiry';

/** Minimal item info shown in the mobile action sheet header (Pinterest-style). */
export interface TileActionInfo {
    title: string;
    subtitle?: string;
    image?: string;
    /** Professional logos render as a circle; product images as a rounded square. */
    imageShape?: 'circle' | 'square';
    /** Background fill behind the image (e.g. a professional's logo background). */
    imageBackground?: string;
}

interface TileActionPanelProps {
    /** The pin target id (item to save). */
    pinId: number;
    /** The library item id. Falls back to pinId. */
    libraryItemId?: number;
    /** The pin type — drives which item the Save flow targets. */
    pinType: PinItemType;
    /**
     * Enquiry config for tiles that have an enquiry target (product /
     * professional). Omit / null to render a More-only panel.
     */
    enquiry?: TileEnquiryConfig | null;
    /** Label for the enquiry action. Defaults to "Enquire". */
    enquiryLabel?: string;
    /** Share target for the Share action. Omit to hide Share. */
    share?: ShareOptions | null;
    /** Item info for the mobile action sheet header. */
    tileInfo?: TileActionInfo;
    /** Disable all actions. */
    disabled?: boolean;
    /** Round the panel corners to match the tile. Defaults to true. */
    rounded?: boolean;
}

/**
 * Desktop Enquire button. Pointing at it resolves the enquiry context, so
 * professionals who don't take enquiries from this visitor drop the button
 * before it can be clicked.
 */
const TileEnquireButton = ({
    label,
    disabled,
    onEnquire,
    onPrefetch,
}: {
    label: string;
    disabled?: boolean;
    onEnquire: () => void;
    onPrefetch: () => void;
}) => (
    <button
        type="button"
        disabled={disabled}
        onPointerEnter={onPrefetch}
        onFocus={onPrefetch}
        onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onEnquire();
        }}
        className="flex h-9 items-center justify-center rounded-full bg-white px-4 text-[0.8125rem] font-medium text-gray-900 shadow-sm transition-colors hover:bg-gray-100 disabled:opacity-60"
    >
        {label}
    </button>
);

/** A row in the mobile action sheet. */
const SheetActionRow = ({
    icon: Icon,
    label,
    onClick,
}: {
    icon: typeof Bookmark;
    label: string;
    onClick: () => void;
}) => (
    <button
        type="button"
        onClick={onClick}
        className="flex w-full items-center gap-3.5 px-5 py-3.5 text-left text-base font-normal text-gray-900 transition-colors active:bg-gray-100"
    >
        <Icon className="size-5 shrink-0" />
        {label}
    </button>
);

/**
 * Enquiry-first tile hover panel, gated behind the `tile_enquiry_panel` flag.
 *
 * - **Desktop** — a hover-revealed `[Enquire] [More]` bar (top-right). More is a
 *   plain, fully-controlled menu (Save + Share). The bar stays visible for the
 *   whole duration of an action (menu open / board-picker open) so it never
 *   vanishes mid-action.
 * - **Mobile** — just a `•••` button (Enquire is tucked inside), opening a
 *   bottom sheet with the item's info (image, title, supplier) and the
 *   Enquire / Save / Share actions, so it's clear which tile the sheet is for.
 *
 * Share opens the app-wide sharing modal (a centred drawer), Save the design
 * board picker.
 */
export const TileActionPanel = ({
    pinId,
    libraryItemId,
    pinType,
    enquiry,
    enquiryLabel = 'Enquire',
    share,
    tileInfo,
    disabled = false,
    rounded = true,
}: TileActionPanelProps) => {
    const { isMobile } = useAtomValue(uiContextAtomDisplayMode);
    const [open, setOpen] = useState(false);
    const panelRef = useRef<HTMLDivElement>(null);
    const menuWrapRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const setShareTarget = useSetAtom(shareTargetAtom);
    const openShareModal = useSetAtom(uiStateAtomSocialSharingModalIsOpened);
    const {
        followSelector,
        openFollowSelector,
        isOpen: followSelectorOpen,
    } = useFollowSelectorProxy({ pinId, pinType, libraryItemId });
    // Owned by the panel, not by the button or sheet row: the mobile row closes
    // the sheet as it opens the modal, and a hook unmounting with the sheet
    // would take the pending enrichment (and the audience withdrawal) with it.
    const {
        onEnquire,
        prefetch: prefetchEnquiry,
        showEnquire,
    } = useTileEnquiry(enquiry ?? null);

    // Resolve the enquiry context as the sheet opens — it settles long before
    // the user has read the sheet and tapped, so a gated professional's row is
    // already gone rather than disappearing under the thumb.
    useEffect(() => {
        if (!open || !isMobile) return;
        prefetchEnquiry();
    }, [open, isMobile, prefetchEnquiry]);

    // Desktop only: close the More dropdown on outside pointer-down / Escape.
    // The mobile bottom sheet manages its own dismissal.
    useEffect(() => {
        if (!open || isMobile) return;
        const onPointerDown = (e: PointerEvent) => {
            if (
                e.target instanceof Node &&
                !menuWrapRef.current?.contains(e.target)
            ) {
                setOpen(false);
            }
        };
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('pointerdown', onPointerDown, true);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('pointerdown', onPointerDown, true);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open, isMobile]);

    const handleSave = useCallback(() => {
        setOpen(false);
        // Anchor the board picker to the panel (top of this tile).
        openFollowSelector(panelRef.current ?? undefined);
    }, [openFollowSelector]);

    const handleShare = useCallback(() => {
        setOpen(false);
        if (!share) return;
        // Tiles pass a relative link; share the absolute item URL.
        const url =
            share.url.startsWith('http') || typeof window === 'undefined'
                ? share.url
                : new URL(share.url, window.location.origin).href;
        const opts = { ...share, url };
        // Mobile: the OS share sheet is a bottom sheet (no anchor issue) and the
        // best UX there. Desktop: the in-app sharing modal.
        if (isMobile && isNativeShareAvailable()) {
            void shareNative(opts);
            return;
        }
        setShareTarget(opts);
        openShareModal(true);
    }, [share, isMobile, setShareTarget, openShareModal]);

    const stop = useCallback((e: React.MouseEvent) => {
        // The whole tile is a link; keep clicks on the panel from navigating.
        e.preventDefault();
        e.stopPropagation();
    }, []);

    // While the sheet is open (and briefly after it closes), swallow clicks on
    // *this tile's* link. Tapping the backdrop to dismiss otherwise falls
    // through to the tile `<a>` underneath and navigates to the detail page. The
    // swallow must be armed for the whole open duration — vaul can close on the
    // click itself, after it has already propagated to the link.
    //
    // Scoped to the tile that owns this panel: the trigger is rendered as a
    // sibling of the tile link, so their common parent bounds the one anchor a
    // dismiss tap can hit. Swallowing every anchor would silently eat the next
    // tap anywhere on the page — another tile, the nav — for 350ms.
    useEffect(() => {
        if (!open || !isMobile) return;
        const tileRoot = triggerRef.current?.parentElement;
        if (!tileRoot) return;
        const swallow = (e: MouseEvent) => {
            if (!(e.target instanceof Element)) return;
            const anchor = e.target.closest('a');
            if (!anchor || !tileRoot.contains(anchor)) return;
            e.preventDefault();
            e.stopPropagation();
        };
        document.addEventListener('click', swallow, true);
        return () => {
            window.setTimeout(() => {
                document.removeEventListener('click', swallow, true);
            }, 350);
        };
    }, [open, isMobile]);

    // ── Mobile: `•••` → bottom sheet with item info + all actions ──────────
    if (isMobile) {
        return (
            <>
                <button
                    ref={triggerRef}
                    type="button"
                    disabled={disabled}
                    aria-label="More actions"
                    onClick={(e) => {
                        stop(e);
                        setOpen(true);
                    }}
                    className="absolute top-2.5 right-2.5 z-10 flex size-9 items-center justify-center rounded-full bg-white text-gray-900 shadow-sm disabled:opacity-60"
                >
                    <MoreHorizontal className="size-4" />
                </button>

                <Drawer open={open} onOpenChange={setOpen} direction="bottom">
                    <DrawerContent noHandle>
                        <DrawerTitle className="sr-only">
                            {tileInfo?.title ?? 'Tile actions'}
                        </DrawerTitle>
                        <DrawerDescription className="hidden" aria-hidden />

                        {tileInfo ? (
                            <div className="flex items-center gap-3 pt-5 pr-14 pb-4 pl-5">
                                {tileInfo.image ? (
                                    <img
                                        src={tileInfo.image}
                                        alt=""
                                        style={
                                            tileInfo.imageBackground
                                                ? {
                                                      backgroundColor:
                                                          tileInfo.imageBackground,
                                                  }
                                                : undefined
                                        }
                                        className={cn(
                                            'size-14 shrink-0',
                                            !tileInfo.imageBackground &&
                                                'bg-[#f3f3f5]',
                                            tileInfo.imageShape === 'circle'
                                                ? 'rounded-full object-contain'
                                                : 'rounded-xl object-cover',
                                        )}
                                    />
                                ) : null}
                                <div className="min-w-0">
                                    <p className="truncate font-medium text-gray-900">
                                        {tileInfo.title}
                                    </p>
                                    {tileInfo.subtitle ? (
                                        <p className="truncate text-sm text-gray-500">
                                            {tileInfo.subtitle}
                                        </p>
                                    ) : null}
                                </div>
                            </div>
                        ) : null}

                        <div className="flex flex-col border-t border-gray-100 pb-[max(env(safe-area-inset-bottom),0.5rem)]">
                            {showEnquire ? (
                                <SheetActionRow
                                    icon={MessageSquareText}
                                    label={enquiryLabel}
                                    onClick={() => {
                                        setOpen(false);
                                        onEnquire();
                                    }}
                                />
                            ) : null}
                            <SheetActionRow
                                icon={Bookmark}
                                label="Save"
                                onClick={handleSave}
                            />
                            {share ? (
                                <SheetActionRow
                                    icon={Share2}
                                    label="Share"
                                    onClick={handleShare}
                                />
                            ) : null}
                        </div>
                    </DrawerContent>
                </Drawer>
                {followSelector}
            </>
        );
    }

    // ── Desktop: hover-revealed [Enquire] [More] bar ───────────────────────
    const actionActive = open || followSelectorOpen;
    return (
        <>
            <div
                ref={panelRef}
                onClick={stop}
                className={cn(
                    'absolute inset-x-0 top-0 z-10 flex items-start justify-end gap-2 p-2.5',
                    'transition-opacity',
                    'bg-linear-to-b from-black/45 to-transparent',
                    actionActive
                        ? 'opacity-100'
                        : 'opacity-100 md:opacity-0 md:group-hover:opacity-100',
                    rounded && 'rounded-t-tile',
                )}
            >
                {showEnquire ? (
                    <TileEnquireButton
                        label={enquiryLabel}
                        disabled={disabled}
                        onEnquire={onEnquire}
                        onPrefetch={prefetchEnquiry}
                    />
                ) : null}

                <div ref={menuWrapRef} className="relative">
                    <button
                        type="button"
                        disabled={disabled}
                        aria-label="More actions"
                        aria-haspopup="menu"
                        aria-expanded={open}
                        onClick={(e) => {
                            stop(e);
                            setOpen((o) => !o);
                        }}
                        className="flex size-9 items-center justify-center rounded-full bg-white text-gray-900 shadow-sm transition-colors hover:bg-gray-100 disabled:opacity-60"
                    >
                        <MoreHorizontal className="size-4" />
                    </button>

                    {open ? (
                        <div
                            role="menu"
                            className="absolute top-full right-0 mt-1.5 min-w-[9.5rem] rounded-xl bg-white p-1.5 shadow-[0_4px_20px_rgba(0,0,0,0.15)]"
                        >
                            <button
                                type="button"
                                role="menuitem"
                                onClick={(e) => {
                                    stop(e);
                                    handleSave();
                                }}
                                className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm font-medium text-gray-900 transition-colors hover:bg-gray-100"
                            >
                                <Bookmark className="size-4" />
                                Save
                            </button>
                            {share ? (
                                <button
                                    type="button"
                                    role="menuitem"
                                    onClick={(e) => {
                                        stop(e);
                                        handleShare();
                                    }}
                                    className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm font-medium text-gray-900 transition-colors hover:bg-gray-100"
                                >
                                    <Share2 className="size-4" />
                                    Share
                                </button>
                            ) : null}
                        </div>
                    ) : null}
                </div>
            </div>
            {followSelector}
        </>
    );
};
